/**
 * Blog Latest Articles Section – custom element
 * Single sticky nav uses links (blog.url / tagged). Filtering by URL (current_tags).
 * List: load more by 1 row (9 cards) per click.
 */
class BlogLatestArticlesSection extends HTMLElement {
  constructor() {
    super();
    this._listGrid = null;
    this._paginationEl = null;
    this._loadMoreBtn = null;
    this._isLoading = false;
    this._promoBanner = null;
    this._promoSticky = null;
    this._promoStickyInner = null;
    this._promoObserver = null;
    this._footerObserver = null;
    this._footerMain = null;
    this._isPromoVisible = true;
    this._isFooterVisible = false;
    this._isViewportAllowedForSticky = true;
    this._boundOnLoadMoreClick = this._onLoadMoreClick.bind(this);
    this._boundOnResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this._cacheRefs();
    this._initPromoSticky();
    this._attachListeners();
    window.addEventListener('resize', this._boundOnResize, { passive: true });
  }

  disconnectedCallback() {
    this._detachListeners();
    this._destroyPromoSticky();
    window.removeEventListener('resize', this._boundOnResize);
    this._listGrid = null;
    this._paginationEl = null;
    this._loadMoreBtn = null;
  }

  _cacheRefs() {
    this._listGrid = this.querySelector('.blog-latest-articles__list-grid');
    this._paginationEl = this.querySelector('.blog-latest-articles__pagination');
    this._loadMoreBtn = this.querySelector('.blog-latest-articles__load-more-btn');
    this._promoBanner = this.querySelector('.blog-latest-articles__promo-banner');
    this._promoSticky = this.querySelector('.blog-latest-articles__promo-sticky');
    this._promoStickyInner = this.querySelector('.blog-latest-articles__promo-sticky-inner');
    this._footerMain = document.querySelector('footer.footer__main');
  }

  _initPromoSticky() {
    if (this.dataset.enablePromoSticky !== 'true') return;
    if (!this._promoBanner || !this._promoSticky || !this._promoStickyInner) return;
    this._isViewportAllowedForSticky = this._isStickyAllowedForViewport();
    this._promoStickyInner.innerHTML = '';
    const promoClone = this._promoBanner.cloneNode(true);
    promoClone.setAttribute('aria-hidden', 'true');
    this._promoStickyInner.appendChild(promoClone);

    this._promoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this._promoBanner) return;
          this._isPromoVisible = entry.isIntersecting;
          this._updatePromoStickyVisibility();
        });
      },
      { threshold: 0.05 }
    );
    this._promoObserver.observe(this._promoBanner);
    this._initFooterObserver();
    this._updatePromoStickyVisibility();
  }

  _initFooterObserver() {
    if (this.dataset.hidePromoStickyOnFooter !== 'true') return;
    if (!this._footerMain) return;
    this._footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this._footerMain) return;
          this._isFooterVisible = entry.isIntersecting;
          this._updatePromoStickyVisibility();
        });
      },
      { threshold: 0.05 }
    );
    this._footerObserver.observe(this._footerMain);
  }

  _destroyPromoSticky() {
    if (this._promoObserver) {
      this._promoObserver.disconnect();
      this._promoObserver = null;
    }
    if (this._footerObserver) {
      this._footerObserver.disconnect();
      this._footerObserver = null;
    }
    if (this._promoSticky) {
      this._promoSticky.classList.remove('translate-y-0', 'opacity-100', 'pointer-events-auto');
      this._promoSticky.classList.add('translate-y-full', 'opacity-0', 'pointer-events-none');
    }
    if (this._promoStickyInner) {
      this._promoStickyInner.innerHTML = '';
    }
    this._isFooterVisible = false;
  }

  _onResize() {
    if (this.dataset.enablePromoSticky !== 'true' || !this._promoSticky) return;
    this._isViewportAllowedForSticky = this._isStickyAllowedForViewport();
    this._updatePromoStickyVisibility();
  }

  _isStickyAllowedForViewport() {
    const visibilityMode = this.dataset.promoStickyVisibility || 'mobile_only';
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (visibilityMode === 'mobile_desktop') return true;
    if (visibilityMode === 'desktop_only') return isDesktop;
    return !isDesktop;
  }

  _updatePromoStickyVisibility() {
    if (!this._promoSticky) return;
    const shouldShow = this._isViewportAllowedForSticky && !this._isPromoVisible && !this._isFooterVisible;
    this._promoSticky.classList.toggle('translate-y-full', !shouldShow);
    this._promoSticky.classList.toggle('opacity-0', !shouldShow);
    this._promoSticky.classList.toggle('pointer-events-none', !shouldShow);
    this._promoSticky.classList.toggle('translate-y-0', shouldShow);
    this._promoSticky.classList.toggle('opacity-100', shouldShow);
    this._promoSticky.classList.toggle('pointer-events-auto', shouldShow);
  }

  _attachListeners() {
    if (this._loadMoreBtn) {
      this._loadMoreBtn.addEventListener('click', this._boundOnLoadMoreClick);
    }
  }

  _detachListeners() {
    if (this._loadMoreBtn) {
      this._loadMoreBtn.removeEventListener('click', this._boundOnLoadMoreClick);
    }
  }

  _setLoadingState(isLoading) {
    if (!this._loadMoreBtn) return;
    this._isLoading = isLoading;
    this._loadMoreBtn.disabled = isLoading;
    const textSpan = this._loadMoreBtn.querySelector('span');
    const iconSpan = this._loadMoreBtn.querySelectorAll('span')[1];
    if (textSpan) {
      textSpan.textContent = isLoading ? 'LOADING...' : 'LOAD MORE ARTICLES';
    }
    if (iconSpan) {
      iconSpan.classList.toggle('hidden', isLoading);
    }
  }

  _replaceLoadMoreButton(nextUrl) {
    if (!this._paginationEl) return;
    if (!nextUrl) {
      this._paginationEl.innerHTML = '';
      this._loadMoreBtn = null;
      return;
    }
    this._paginationEl.innerHTML =
      '<button type="button" class="blog-latest-articles__load-more-btn rdm-btn-primary mt-0 max-w-[312px] min-w-[204px] md:min-w-[376px]" data-next-url="' +
      nextUrl +
      '">' +
      '<span>LOAD MORE ARTICLES</span>' +
      '<span>' +
      '<svg width="22" height="14" viewBox="0 0 22 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M0 6.83203H19.6875M19.6875 6.83203L13.5625 0.707031M19.6875 6.83203L13.5625 12.957" stroke="white" stroke-width="2"></path>' +
      '</svg>' +
      '</span>' +
      '</button>';
    this._loadMoreBtn = this._paginationEl.querySelector('.blog-latest-articles__load-more-btn');
    if (this._loadMoreBtn) {
      this._loadMoreBtn.addEventListener('click', this._boundOnLoadMoreClick);
    }
  }

  async _onLoadMoreClick(event) {
    const loadMoreBtn = event.currentTarget;
    if (!loadMoreBtn || this._isLoading || !this._listGrid) return;
    const nextUrl = loadMoreBtn.getAttribute('data-next-url');
    if (!nextUrl) return;

    this._setLoadingState(true);
    try {
      const response = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Failed to load next page');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextCards = doc.querySelectorAll('.blog-latest-articles__list-card');
      nextCards.forEach((card) => {
        this._listGrid.appendChild(card);
      });

      const nextButton = doc.querySelector('.blog-latest-articles__load-more-btn');
      const upcomingUrl = nextButton ? nextButton.getAttribute('data-next-url') : null;
      this._detachListeners();
      this._replaceLoadMoreButton(upcomingUrl);
    } catch (error) {
      this._setLoadingState(false);
      console.error('[blog-latest-articles] Load more failed', error);
      return;
    }

    this._setLoadingState(false);
  }
}

if (!customElements.get('blog-latest-articles-section')) {
  customElements.define('blog-latest-articles-section', BlogLatestArticlesSection);
}
